/**
 * OpeningHoursSelector Component
 *
 * Specialized component for managing branch opening hours with German UI labels.
 * Matches exact UI specification: Von/Bis/Pause von/Pause bis structure.
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  businessHourPresets,
  getDayOfWeekOptions,
  timeSlotOptions,
} from '@/constants/branchOptions';
import { useFormsTranslation } from '@/hooks/useTranslation';
import type {
  DaySchedule,
  OpeningHours,
  OpeningHoursSelectorProps,
} from '@/types/branch.types';
import { Copy, RotateCcw } from 'lucide-react';
import React from 'react';

const OpeningHoursSelector: React.FC<OpeningHoursSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { t: tForms } = useFormsTranslation();

  // Helper function to update a specific day's schedule
  const updateDaySchedule = (day: string, updates: Partial<DaySchedule>) => {
    const currentDay = value[day as keyof OpeningHours];

    // If disabling a day, clear the time values
    const finalUpdates =
      updates.enabled === false
        ? {
            ...updates,
            openTime: '',
            closeTime: '',
          }
        : updates;

    const newOpeningHours = {
      ...value,
      [day]: {
        ...currentDay,
        ...finalUpdates,
      },
    };
    onChange(newOpeningHours);
  };

  // Apply schedule to all weekdays (Monday-Friday)
  const applyToWeekdays = (schedule: DaySchedule) => {
    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const newOpeningHours = { ...value };

    weekdays.forEach(day => {
      newOpeningHours[day as keyof OpeningHours] = { ...schedule };
    });

    onChange(newOpeningHours);
  };

  // Apply schedule to all days
  const applyToAllDays = (schedule: DaySchedule) => {
    const allDays = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ];
    const newOpeningHours = { ...value };

    allDays.forEach(day => {
      newOpeningHours[day as keyof OpeningHours] = { ...schedule };
    });

    onChange(newOpeningHours);
  };

  // Apply business hours preset
  const applyPreset = (presetValue: string) => {
    const preset = businessHourPresets.find(p => p.value === presetValue);
    if (preset) {
      const schedule: DaySchedule = {
        enabled: true,
        workingStart: preset.hours.from,
        workingEnd: preset.hours.to,
        breakStart: '12:00',
        breakEnd: '13:00',
      };
      applyToWeekdays(schedule);
    }
  };

  // Reset all opening hours
  const resetAllHours = () => {
    const emptySchedule: DaySchedule = {
      enabled: false,
      workingStart: '',
      workingEnd: '',
      breakStart: '',
      breakEnd: '',
    };
    applyToAllDays(emptySchedule);
  };

  return (
    <>
      {/* Preset and Bulk Actions */}
      <div className='flex flex-wrap gap-2'>
        <Select onValueChange={applyPreset} disabled={disabled}>
          <SelectTrigger className='w-[200px]'>
            <SelectValue
              placeholder={tForms(
                'branch.dialog.openingHours.templatePlaceholder'
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {businessHourPresets.map(preset => (
              <SelectItem key={preset.value} value={preset.value}>
                {preset.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          type='button'
          variant='outline'
          size='sm'
          onClick={resetAllHours}
          disabled={disabled}
          className='flex items-center gap-1'
        >
          <RotateCcw className='h-4 w-4' />
          {tForms('branch.dialog.openingHours.resetButton')}
        </Button>
      </div>
      {getDayOfWeekOptions(tForms).map(dayOption => {
        const daySchedule = value[dayOption.value as keyof OpeningHours] || {
          enabled: false,
          workingStart: '',
          workingEnd: '',
          breakStart: '',
          breakEnd: '',
        };

        return (
          <div
            key={dayOption.value}
            className='border rounded-lg p-4 space-y-3'
          >
            {/* Day Header */}
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-3'>
                <Checkbox
                  id={`${dayOption.value}-enabled`}
                  checked={daySchedule.enabled || false}
                  onCheckedChange={checked =>
                    updateDaySchedule(dayOption.value, { enabled: !!checked })
                  }
                  disabled={disabled}
                />
                <Label
                  htmlFor={`${dayOption.value}-enabled`}
                  className='font-medium'
                >
                  {dayOption.label}
                </Label>
                {daySchedule.enabled && (
                  <Badge variant='secondary' className='text-xs'>
                    {tForms('branch.dialog.openingHours.activeBadge')}
                  </Badge>
                )}
              </div>

              {/* Copy Actions */}
              {daySchedule.enabled && !disabled && (
                <div className='flex gap-1'>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={() => applyToWeekdays(daySchedule)}
                    className='text-xs'
                  >
                    <Copy className='h-3 w-3 mr-1' />
                    {tForms('branch.dialog.openingHours.copyToWeekdays')}
                  </Button>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={() => applyToAllDays(daySchedule)}
                    className='text-xs'
                  >
                    <Copy className='h-3 w-3 mr-1' />
                    {tForms('branch.dialog.openingHours.copyToAllDays')}
                  </Button>
                </div>
              )}
            </div>

            {/* Time Inputs - Only show if day is enabled */}
            {daySchedule.enabled && (
              <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                {/* Working Hours */}
                <div className='space-y-1'>
                  <Label
                    htmlFor={`${dayOption.value}-from`}
                    className='text-sm text-muted-foreground'
                  >
                    {tForms('branch.dialog.openingHours.labels.from')}
                  </Label>
                  <Select
                    value={daySchedule.workingStart || ''}
                    onValueChange={value =>
                      updateDaySchedule(dayOption.value, {
                        workingStart: value,
                      })
                    }
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='--:--' />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlotOptions.map(time => (
                        <SelectItem key={time.value} value={time.value}>
                          {time.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className='space-y-1'>
                  <Label
                    htmlFor={`${dayOption.value}-to`}
                    className='text-sm text-muted-foreground'
                  >
                    {tForms('branch.dialog.openingHours.labels.to')}
                  </Label>
                  <Select
                    value={daySchedule.workingEnd || ''}
                    onValueChange={value =>
                      updateDaySchedule(dayOption.value, { workingEnd: value })
                    }
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='--:--' />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlotOptions.map(time => (
                        <SelectItem key={time.value} value={time.value}>
                          {time.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Break Hours */}
                <div className='space-y-1'>
                  <Label
                    htmlFor={`${dayOption.value}-pause-from`}
                    className='text-sm text-muted-foreground'
                  >
                    {tForms('branch.dialog.openingHours.labels.breakFrom')}
                  </Label>
                  <Select
                    value={daySchedule.breakStart || ''}
                    onValueChange={value =>
                      updateDaySchedule(dayOption.value, { breakStart: value })
                    }
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='--:--' />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlotOptions.map(time => (
                        <SelectItem key={time.value} value={time.value}>
                          {time.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className='space-y-1'>
                  <Label
                    htmlFor={`${dayOption.value}-pause-to`}
                    className='text-sm text-muted-foreground'
                  >
                    {tForms('branch.dialog.openingHours.labels.breakTo')}
                  </Label>
                  <Select
                    value={daySchedule.breakEnd || ''}
                    onValueChange={value =>
                      updateDaySchedule(dayOption.value, { breakEnd: value })
                    }
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='--:--' />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlotOptions.map(time => (
                        <SelectItem key={time.value} value={time.value}>
                          {time.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default OpeningHoursSelector;
